import { Box, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import PatientDetailsHeader from '../../../components/Account/PatientDetailsHeader';
import BackButton from '../../../components/BackButton';
import MultiDatePicker from '../../../components/documented/MultiDatePicker/MultiDatePicker';
import {
  convertDate,
  getEnumKeyByEnumValue,
} from '../../../helpers/utilityFunctions/utilityFunctions';
import { useMonthlyDatePicker } from '../../../hooks/useMonthlyDatePicker';
import { ActiveTestOverview } from '../../../model/active/activeTestOverview';
import { ActiveType } from '../../../model/activeType';
import {
  clearActiveTestData,
  clearActiveTestDate,
  clearActiveTestDates,
  getActiveTestData,
  setActiveTestDate,
} from '../../../redux/actions/activeActions';
import { useAppThunkDispatch } from '../../../redux/configureStore';
import { latestOverviewData } from '../../../redux/selectors/activeSelectors';
import { selectedTestDateTime } from '../../../redux/selectors/patientSelectors';
import { activeTestService } from '../../../services/activeTestService';
interface Props {
  patientId?: string;
  testType: ActiveType;
}

export function getActiveTestMetricDate(
  activeType: ActiveType,
  overview: ActiveTestOverview
): string | undefined {
  switch (activeType) {
    case ActiveType._TUG:
      return overview.latestTUG.date;
    case ActiveType._6MWT:
      return overview.latest6MWT.date;
    case ActiveType._10MWT:
      return overview.latest10MWT.date;
    case ActiveType._BT:
      return overview.latestBT.date;
    case ActiveType._CTEST:
      return overview.latestCustom.dateTime;
    default:
      return undefined; // Handle unknown ActiveType values
  }
}

export const TestDetailHeader: React.FC<Props> = ({ patientId, testType }) => {
  const { t } = useTranslation('tests');
  const dispatch = useAppThunkDispatch();

  const latestOverViewData = useSelector(latestOverviewData);
  const testDateTime = useSelector(selectedTestDateTime);

  const processedType = getEnumKeyByEnumValue(ActiveType, testType)!.substring(
    1
  );
  const processedTypeWithUnderscore = getEnumKeyByEnumValue(
    ActiveType,
    testType
  );

  //Derived state
  const testSelected = testDateTime !== '';

  // The most-recent test date for this patient/type, per the overview. This is
  // the authoritative starting day; it is intentionally preferred over any
  // previously selected date so a different patient's day can never carry over.
  const mostRecent = latestOverViewData
    ? getActiveTestMetricDate(testType, latestOverViewData)
    : undefined;

  const {
    dates,
    selectedDate,
    isLoading,
    setSelectedDate,
    setSelectedMonth,
  } = useMonthlyDatePicker({
    patientId,
    // Re-initialise per patient and per test type, and again once the overview
    // (hence the real most-recent date) arrives after a hard refresh.
    resetKey: `${patientId ?? ''}|${testType}|${mostRecent ?? ''}`,
    enabled: !testSelected,
    getInitialDate: () => (mostRecent ? new Date(mostRecent) : new Date()),
    resolveInitialDate: (_monthDates, hint) => hint,
    fetchMonth: (range) =>
      activeTestService.GetActiveTestDatesByRange(
        patientId || '',
        processedType,
        range.startDate,
        range.endDate
      ),
    onDaySelected: ({ day, hasData }) => {
      dispatch(setActiveTestDate(day));
      if (hasData) {
        dispatch(
          getActiveTestData(
            patientId || '',
            processedTypeWithUnderscore || '',
            convertDate(day)
          )
        );
      } else {
        dispatch(clearActiveTestData());
      }
    },
    onReset: () => {
      dispatch(clearActiveTestDates());
      dispatch(clearActiveTestData());
      dispatch(clearActiveTestDate());
    },
  });

  return (
    <Box>
      <PatientDetailsHeader selectedPatientId={patientId} />
      <BackButton />
      <Box sx={{ display: 'flex', flexDirection: 'row', mt: 1 }}>
        <Typography variant="h5" sx={{ mt: 1, pr: 2 }}>
          <strong>
            {(() => {
              switch (testType) {
                case ActiveType._10MWT:
                  return t('titles.tenMetreWalk');
                case ActiveType._TUG:
                  return t('titles.timedUpAndGo');
                case ActiveType._CTEST:
                  return t('titles.custom');
                default:
                  return t('titles.active');
              }
            })()}
          </strong>
        </Typography>

        {testSelected ? (
          <Typography variant="h5" sx={{ mt: 1, pr: 2 }}>
            {new Date(testDateTime).toLocaleString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            })}
          </Typography>
        ) : (
          <MultiDatePicker
            dates={dates}
            selectedDate={selectedDate}
            isLoading={isLoading}
            setSelectedDate={setSelectedDate}
            setSelectedMonth={setSelectedMonth}
          />
        )}
      </Box>
    </Box>
  );
};

export default TestDetailHeader;
